import { useRef } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createTrackingUpdate } from "api";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { CustomFileType } from "hooks/useFileHook";
import errorToast from "helpers/errorToast";

type ActivityFieldActionOptions = {
  shipmentId: string;
  files: CustomFileType[];
  resetFiles: () => void;
};

export default function useActivityFieldActions({ shipmentId, files, resetFiles }: ActivityFieldActionOptions) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { mutate: handleSubmitTrackingUpdate, isLoading: isSubmitting } = useMutation({
    mutationFn: () =>
      createTrackingUpdate(shipmentId, {
        type: files.length > 0 ? "upload" : "general",
        message: textAreaRef?.current?.value ?? "",
        attachments: files.map((f) => ({
          filename: f.file.name,
          size: f.file.size,
          url: f.url,
          reference_number: f.reference_number,
        })),
      }),
    onSuccess: async () => {
      toast(
        intl.formatMessage({
          id: "tracking.activityField.createSuccess",
          defaultMessage: "Successfully submitted.",
        }),
        { type: "success" },
      );
      textAreaRef.current!.value = "";
      resetFiles();
      await new Promise((resolve) => {
        setTimeout(resolve, 750);
      }).then(() =>
        queryClient.invalidateQueries({
          queryKey: shipmentQueryKeys.shipmentGeoDataDetails(shipmentId),
        }),
      );
    },
    onError: (err) => {
      errorToast(
        err ??
          intl.formatMessage({
            id: "tracking.activityField.createFail",
            defaultMessage: "Fail to submit. Please try again later.",
          }),
      );
    },
  });

  return {
    textAreaRef,
    handleSubmitTrackingUpdate,
    isSubmitting,
  };
}
